import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {compose} from 'recompose';
import axios from 'axios';
import {addCommas} from '../shared/functions';
import {getGetHeaders} from '../shared/serverFunctions';
import {checkTokenStatus} from '../shared/tokenFunctions';
import {RESOURCES as reportsResources} from '../fuseReportsResourceMap';
import DetailedSalesReportPaymentSummaryTable from './DetailedSalesReportPaymentSummaryTable';
import DetailedSalesReportSalesSummaryTable from './DetailedSalesReportSalesSummaryTable';
import '../shared/react-table.css';
import Config from '../messages';

/**
 * Detailed Sales class for retrieving and displaying Detailed Sales Summary in multiple tables
 *
 */
class DetailedSalesAndSummaryReportSet extends Component {
    constructor(props) {
        super(props);
        this.state = {
            dataProcessed: false,
            errorMessage: null,
            paymentSummaryComplete: false,
            paymentSummaryList: [],
            paymentTotals: {
                products: 0,
                transactions: 0,
                units: 0
            },
            reportName: 'Detailed Sales',
            salesSummaryComplete: false,
            salesSummaryList: [],
            summaryComplete: false,
        };
    }

    componentWillMount() {
        this.fetchData();
    }

    componentWillReceiveProps(nextProps) {
        let clearTable = false;
        if (
            nextProps.location !== this.props.location ||
            nextProps.startDate !== this.props.startDate ||
            nextProps.endDate !== this.props.endDate ||
            nextProps.offset !== this.props.offset
        ) {
            clearTable = true;
        }
        if (clearTable) {
            this.setState({
                dataProcessed: false,
                errorMessage: null,
                paymentSummaryList: [],
                paymentTotals: {
                    products: 0,
                    transactions: 0,
                    units: 0
                },
                salesSummaryList: [],
                summaryComplete: false
            });
        }
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.location !== this.props.location ||
            prevProps.startDate !== this.props.startDate ||
            prevProps.endDate !== this.props.endDate ||
            prevProps.offset !== this.props.offset
        ) {
            this.fetchData();
        }
    }

    /* API Calls ------------------------------ */

    /**
     * Get Sales data for a specific date range
     *
     */
    fetchData() {
        // Assemble the URI
        const params = {
            location_id: this.props.location.id,
            start_date: this.props.startDate,
            end_date: this.props.endDate,
            offset: this.props.offset,
        };
        const resource = reportsResources.DetailedSales.GET_LIST(params);

        const tokenStatus = checkTokenStatus(null);
        if (tokenStatus.expired) {
            return;
        }

        axios({
            method: 'get',
            url: resource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            if (result.data && result.data.payload) {
                this.processData(result.data.payload);
            }
        }).catch((error) => {
            let message = Config.fuse.error.error + this.state.reportName + '.';
            if (error.response) {
                // Server response outside 2xx
                if (error.response.data && error.response.data.error) {
                    message = Config.fuse.error.error + this.state.reportName + ': ' + error.response.data.error;
                }
            } else if (error.request) {
                // No response
                message = Config.fuse.error.no_response + this.state.reportName + '.';
            } else {
                console.log('Error', error.message);
            }
            this.setState({
                dataProcessed: true,
                errorMessage: message,
                paymentSummaryComplete: true,
                paymentSummaryList: [],
                paymentTotals: {
                    products: 0,
                    transactions: 0,
                    units: 0
                },
                salesSummaryComplete: true,
                summaryComplete: false,
                salesSummaryList: []
            }, () => {
                if (typeof this.props.onError === 'function') {
                    this.props.onError(message);
                }
            });
        });
    }

    /* Data Processing ------------------------------ */

    /**
     * Store transaction/type results
     *
     * @param data
     */
    processData(data) {
        const aggs = data.aggregations;
        let paymentSummaryList = [];
        let salesSummaryList = [];
        let totalProducts = 0;
        let totalUnitsSold = 0;
        let transactions = 0;

        if (aggs) {
            // Assemble payment totals for Products, Units, Transactions
            if (aggs.product_unit_sales) {
                totalProducts = (aggs.product_unit_sales.total_products && aggs.product_unit_sales.total_products.value) ? addCommas(aggs.product_unit_sales.total_products.value) : 0;
                totalUnitsSold = (aggs.product_unit_sales.total_product_qty && aggs.product_unit_sales.total_product_qty.value) ? addCommas(aggs.product_unit_sales.total_product_qty.value) : 0;
            }
            transactions = (aggs.payments.transactions && aggs.payments.transactions.value) ? addCommas(aggs.payments.transactions.value) : 0;

            // Assemble and store Payment Summary data for table component
            if (aggs.payments && aggs.payments.by_payment && aggs.payments.by_payment.buckets) {
                let pResults = aggs.payments.by_payment.buckets;
                if (pResults) {
                    paymentSummaryList = pResults;
                }
            }

            // Assemble and store Product Unit Sales Summary data for table component
            if (aggs.product_unit_sales && aggs.product_unit_sales.by_product_unit && aggs.product_unit_sales.by_product_unit.buckets) {
                let sResults = aggs.product_unit_sales.by_product_unit.buckets;
                if (sResults) {
                    salesSummaryList = sResults;
                }
            }
        }

        // Finalize table data
        this.setState({
            dataProcessed: true,
            paymentSummaryList: paymentSummaryList,
            paymentTotals: {
                products: totalProducts,
                transactions: transactions,
                units: totalUnitsSold
            },
            salesSummaryList: salesSummaryList
        }, () => {
            this.checkSummaryComplete();
        });
    }

    checkSummaryComplete = () => {
        if (this.state.paymentSummaryComplete && this.state.salesSummaryComplete) {
            this.setState({
                summaryComplete: true
            });
        }
    };

    handleSummaryComplete = (summary, isComplete) => {
        switch (summary) {
            case 'payment':
                this.setState({
                    paymentSummaryComplete: isComplete,
                }, () => {
                    this.checkSummaryComplete();
                });
                break;
            case 'sales':
                this.setState({
                    salesSummaryComplete: isComplete,
                }, () => {
                    this.checkSummaryComplete();
                });
                break;
            default:
                break;
        }
    };

    render() {

        const {
            endDate,
            location,
            startDate,
            system
        } = this.props;

        const {
            dataProcessed,
            errorMessage,
            paymentSummaryList,
            paymentTotals,
            salesSummaryList
        } = this.state;

        return (
            <div>
                {dataProcessed &&
                    <div>
                        {/* Table: Payment Summary */}
                        <DetailedSalesReportPaymentSummaryTable
                            listData={paymentSummaryList}
                            locationId={location.id.toString()}
                            startDate={startDate}
                            endDate={endDate}
                            system={system}
                            onSummaryComplete={this.handleSummaryComplete}
                        />

                        {/* Table: Sales by Product Unit */}
                        <DetailedSalesReportSalesSummaryTable
                            listData={salesSummaryList}
                            locationId={location.id.toString()}
                            paymentTotals={paymentTotals}
                            startDate={startDate}
                            endDate={endDate}
                            system={system}
                            onSummaryComplete={this.handleSummaryComplete}
                        />
                    </div>
                }

                {/* Error and Messaging */}
                {errorMessage &&
                    <div className="error">
                        <p>{errorMessage}</p>
                    </div>
                }
            </div>
        )
    }
}

DetailedSalesAndSummaryReportSet.propTypes = {
    addField: PropTypes.bool,
    endDate: PropTypes.string.isRequired,
    location: PropTypes.object.isRequired,
    offset: PropTypes.string.isRequired,
    onDisplayMessage: PropTypes.func,
    onError: PropTypes.func,
    startDate: PropTypes.string.isRequired,
    system: PropTypes.string,
};

DetailedSalesAndSummaryReportSet.defaultProps = {
    addField: true,
    endDate: null,
    location: {
        id: null,
        name: '',
        city: '',
        state: ''
    },
    offset: null,
    startDate: null,
};

const enhance = compose(
    connect()
);

export default enhance(DetailedSalesAndSummaryReportSet);



// WEBPACK FOOTER //
// ./src/Fuse/components/DetailedSalesAndSummaryReportSet.js